import { CONFIG } from '../../core/config.js'

// Boot-blocking paths (callback exchange, handoff redeem, session refresh)
// must never hang on a stalled network — an unbounded fetch leaves the app
// on the pre-mount placeholder forever with no error event (bao 2026-10-24:
// stuck /auth/callback page). Local helper instead of api/index.js's
// fetchWithTimeout: api/index.js imports THIS module (client id), so
// importing back would create a cycle. Injected fetchers (tests) pass
// through unbounded — the caller owns them.
const AUTH_FETCH_TIMEOUT_MS = 15_000
function boundFetcher(fetcher) {
  if (fetcher !== fetch) return fetcher
  return (url, options = {}) => {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), AUTH_FETCH_TIMEOUT_MS)
    return fetch(url, { ...options, signal: controller.signal }).finally(() =>
      clearTimeout(timer)
    )
  }
}

// Canonical customer realm client (requirements-v1 §3 Option A) — the ONLY
// customer auth chain. H-2026-10-16-175 removed the Vite flag gating: the
// authorization-code flow is always live, no legacy fallback remains.
export const CREATOR_HUB_CANONICAL_CLIENT_ID = 'customer-creator-hub'
export const CREATOR_HUB_CANONICAL_REDIRECT_URI = 'https://creator.mamamiya.store/auth/callback'
const VERIFIER_KEY = 'mms_creator_pkce_verifier'
const REFRESH_KEY = 'mms_customer_refresh_token'
const TOKEN_KEY = 'mms_customer_token'
const CUSTOMER_KEY = 'mms_customer'

function randomVerifier() {
  const bytes = crypto.getRandomValues(new Uint8Array(32))
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

async function pkceChallenge(verifier) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  let binary = ''
  for (const byte of new Uint8Array(digest)) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function clearCustomerAuth() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(CUSTOMER_KEY)
  localStorage.removeItem(REFRESH_KEY)
  localStorage.removeItem('mms_token_expires')
}

function customerFromSession(payload) {
  const data = payload?.data
  if (!data || data.realm !== 'customer' || data.audience !== 'customer' || !data.customer)
    throw new Error('Customer session rejected')
  return data.customer
}

export async function createCreatorHubCanonicalAuthorizeUrl(opts = {}, fetcher = fetch) {
  // Back-compat: the legacy signature was (fetcher) — a function first arg
  // is the fetcher, not options (H-186 added the options slot).
  if (typeof opts === 'function') {
    fetcher = opts
    opts = {}
  }
  fetcher = boundFetcher(fetcher)
  const response = await fetcher(
    `${CONFIG.CUSTOMER_API}/api/v1/auth/providers?client_id=${CREATOR_HUB_CANONICAL_CLIENT_ID}`
  )
  const payload = await response.json()
  if (!response.ok || !payload.success || payload.data?.realm !== 'customer')
    throw new Error('Customer authorization is unavailable')
  const verifier = randomVerifier()
  const state = randomVerifier()
  sessionStorage.setItem(VERIFIER_KEY, verifier)
  const query = new URLSearchParams({
    response_type: 'code',
    client_id: CREATOR_HUB_CANONICAL_CLIENT_ID,
    redirect_uri: CREATOR_HUB_CANONICAL_REDIRECT_URI,
    state,
    code_challenge: await pkceChallenge(verifier),
    code_challenge_method: 'S256',
  })
  // H-186: non-default providers ride the additive `provider` param; google
  // omits it so the URL works against pre-H-185 backends too (their default
  // IS google, and an unknown param would be the only risk).
  const provider = typeof opts.provider === 'string' ? opts.provider : ''
  if (provider && provider !== 'google') query.set('provider', provider)
  return `${CONFIG.CUSTOMER_API}/api/v1/auth/authorize?${query}`
}

/**
 * H-186 provider-driven login UI: consume the /providers descriptor array
 * (H-185 contract — data.oauth_providers: [{ key, display_name, icon? }]).
 * Returns the normalized descriptor list, or null on ANY failure (network
 * throw, non-ok, success !== true, missing/non-array/empty field) — null
 * means "caller falls back to the static Google button". Single-flight:
 * overlapping callers share one in-flight GET; failures never poison the
 * next call.
 */
let providersFlight = null
export function fetchCreatorHubOauthProviders(fetcher = fetch) {
  fetcher = boundFetcher(fetcher)
  if (providersFlight) return providersFlight
  providersFlight = (async () => {
    try {
      const response = await fetcher(
        `${CONFIG.CUSTOMER_API}/api/v1/auth/providers?client_id=${CREATOR_HUB_CANONICAL_CLIENT_ID}`
      )
      const payload = await response.json()
      if (!response.ok || !payload?.success) return null
      const list = payload?.data?.oauth_providers
      if (!Array.isArray(list)) return null
      const seen = new Set()
      const out = []
      for (const entry of list) {
        if (!entry || typeof entry.key !== 'string' || !entry.key) continue
        if (typeof entry.display_name !== 'string' || !entry.display_name) continue
        if (seen.has(entry.key)) continue
        seen.add(entry.key)
        out.push(entry)
      }
      return out.length ? out : null
    } catch {
      return null
    }
  })().finally(() => {
    providersFlight = null
  })
  return providersFlight
}

export async function completeCreatorHubCanonicalCallback(search, fetcher = fetch) {
  fetcher = boundFetcher(fetcher)
  const params = new URLSearchParams(search)
  const providerCode = params.get('code')
  const state = params.get('state')
  const verifier = sessionStorage.getItem(VERIFIER_KEY)
  if (params.get('error') || !providerCode || !state || !verifier) {
    clearCustomerAuth()
    return { ok: false, code: 'INVALID_CALLBACK' }
  }
  try {
    const callback = await fetcher(
      `${CONFIG.CUSTOMER_API}/api/v1/auth/callback?${new URLSearchParams({ code: providerCode, state })}`
    )
    const callbackPayload = await callback.json()
    const internalCode = callbackPayload?.data?.code
    if (!callback.ok || !callbackPayload.success || !internalCode)
      throw new Error('callback rejected')
    const tokenResponse = await fetcher(`${CONFIG.CUSTOMER_API}/api/v1/auth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        grant_type: 'authorization_code',
        client_id: CREATOR_HUB_CANONICAL_CLIENT_ID,
        code: internalCode,
        code_verifier: verifier,
        redirect_uri: CREATOR_HUB_CANONICAL_REDIRECT_URI,
      }),
    })
    const tokenPayload = await tokenResponse.json()
    if (!tokenResponse.ok || !tokenPayload.success || !tokenPayload.data?.token)
      throw new Error('token rejected')
    const sessionResponse = await fetcher(`${CONFIG.CUSTOMER_API}/api/v1/auth/session`, {
      headers: { Authorization: `Bearer ${tokenPayload.data.token}` },
    })
    const customer = customerFromSession(await sessionResponse.json())
    localStorage.setItem(TOKEN_KEY, tokenPayload.data.token)
    if (tokenPayload.data.refresh_token)
      localStorage.setItem(REFRESH_KEY, tokenPayload.data.refresh_token)
    if (tokenPayload.data.expires_at)
      localStorage.setItem('mms_token_expires', String(tokenPayload.data.expires_at))
    localStorage.setItem(CUSTOMER_KEY, JSON.stringify(customer))
    sessionStorage.removeItem(VERIFIER_KEY)
    return { ok: true, customer }
  } catch {
    clearCustomerAuth()
    return { ok: false, code: 'EXCHANGE_FAILED' }
  }
}

/**
 * H-180: redeem a one-time handoff code for a full token pair (access +
 * 30d rotating refresh). Replaces the raw access-token handoff, whose
 * embedded sessions died at the <=24h access expiry (REQ-CH-43 gap). The
 * exchange response carries the customer row, so no extra /session call is
 * needed. Persistence is best-effort: storage-denied embeds keep the
 * session in memory (saveAuth applies the same degraded path).
 */
export async function exchangeCreatorHubHandoffCode(code, fetcher = fetch) {
  fetcher = boundFetcher(fetcher)
  let data
  try {
    const response = await fetcher(`${CONFIG.CUSTOMER_API}/api/v1/auth/handoff-code/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ client_id: CREATOR_HUB_CANONICAL_CLIENT_ID, code }),
    })
    const payload = await response.json()
    if (!response.ok || !payload.success || !payload.data?.token || !payload.data?.customer)
      throw new Error('handoff rejected')
    data = payload.data
  } catch {
    return { ok: false, code: 'EXCHANGE_FAILED' }
  }
  try {
    localStorage.setItem(TOKEN_KEY, data.token)
    if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token)
    if (data.expires_at) localStorage.setItem('mms_token_expires', String(data.expires_at))
    localStorage.setItem(CUSTOMER_KEY, JSON.stringify(data.customer))
  } catch {
    /* storage unavailable — the in-memory session stays usable */
  }
  return { ok: true, customer: data.customer, token: data.token }
}

export async function refreshCreatorHubCanonicalSession(fetcher = fetch) {fetcher = boundFetcher(fetcher)
  const refreshToken = localStorage.getItem(REFRESH_KEY)
  if (!refreshToken) return false
  try {
    const response = await fetcher(`${CONFIG.CUSTOMER_API}/api/v1/auth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        grant_type: 'refresh_token',
        client_id: CREATOR_HUB_CANONICAL_CLIENT_ID,
        refresh_token: refreshToken,
      }),
    })
    const payload = await response.json()
    if (!response.ok || !payload.success || !payload.data?.token || !payload.data?.refresh_token)
      throw new Error('refresh rejected')
    localStorage.setItem(TOKEN_KEY, payload.data.token)
    localStorage.setItem(REFRESH_KEY, payload.data.refresh_token)
    if (payload.data.expires_at)
      localStorage.setItem('mms_token_expires', String(payload.data.expires_at))
    return true
  } catch {
    clearCustomerAuth()
    return false
  }
}

export async function logoutCreatorHubCanonicalSession(fetcher = fetch) {
  fetcher = boundFetcher(fetcher)
  const token = localStorage.getItem(TOKEN_KEY)
  if (token)
    await fetcher(`${CONFIG.CUSTOMER_API}/api/v1/auth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {})
  clearCustomerAuth()
}

export function recordCreatorAuthMetric(event, outcome, reason, extra) {
  window.dispatchEvent(
    new CustomEvent('mms:auth-metric', {
      detail: {
        client: CREATOR_HUB_CANONICAL_CLIENT_ID,
        realm: 'customer',
        event,
        outcome,
        ...(reason ? { reason } : {}),
        // H-186: additive detail fields (e.g. { provider }) — never renames.
        ...(extra && typeof extra === 'object' ? extra : {}),
        timestamp: Date.now(),
      },
    })
  )
}
